import { useEffect, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { api, formatApiErrorDetail } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { toast } from "sonner";

function formatTs(iso) {
  if (!iso) return "";
  try {
    return iso.replace("T", " · ").slice(0, 16) + " UTC";
  } catch {
    return iso;
  }
}

function Lightbox({ src, onClose }) {
  // Esc to close
  useEffect(() => {
    const onKey = (e) => { if (e.key === "Escape") onClose(); };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [onClose]);

  return (
    <div
      className="fixed inset-0 z-[150] bg-[#000]/95 backdrop-blur-sm flex items-center justify-center p-4 cursor-zoom-out"
      onClick={onClose}
      data-testid="terra-lightbox"
    >
      <div className="absolute top-4 right-4 text-[0.65rem] tracking-[0.3em] uppercase text-[#A3A3A3]">
        click anywhere · esc to close
      </div>
      <img
        src={src}
        alt="dispatch plate"
        className="max-w-full max-h-full object-contain"
        onClick={(e) => e.stopPropagation()}
      />
    </div>
  );
}

export default function TerraDispatchDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { isAdmin, user } = useAuth();
  const [capture, setCapture] = useState(null);
  const [lightboxSrc, setLightboxSrc] = useState(null);

  useEffect(() => {
    if (user === null) return;
    api.get(`/terra-chronicle/${id}`)
      .then((r) => setCapture(r.data))
      .catch(() => setCapture(false));
  }, [id, user]);

  const onDelete = async () => {
    if (!window.confirm(`Burn dispatch ${capture.id}?`)) return;
    try {
      await api.delete(`/terra-chronicle/${capture.id}`);
      toast.success("Dispatch burned.");
      navigate("/the-terra-chronicle");
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Could not burn.");
    }
  };

  if (capture === null) {
    return <div className="text-[#555] text-xs tracking-widest uppercase">::: opening the dispatch :::</div>;
  }

  if (capture === false) {
    return (
      <div className="border border-[#333] p-12 text-center">
        <div className="font-serif-display text-3xl mb-4">Dispatch not filed.</div>
        <Link to="/the-terra-chronicle" className="tiv-btn">↩ Return to the chronicle</Link>
      </div>
    );
  }

  return (
    <div className="space-y-10" data-testid="terra-detail-page">
      <div className="text-[0.65rem] tracking-[0.35em] uppercase text-[#A3A3A3]">
        <Link to="/the-terra-chronicle" className="hover:text-[#E3E1D9]">↖ Chronicle</Link>
        <span className="mx-3 text-[#555]">/</span>
        <span>{capture.id}</span>
      </div>

      <header className="space-y-4 border-b border-[#333] pb-8">
        <div className="flex flex-wrap items-center gap-3 text-[0.6rem] tracking-[0.3em] uppercase">
          <span className="text-[#A3A3A3]">DISPATCH</span>
          <span className="text-[#E3E1D9]">{capture.id}</span>
          {capture.source_date && (
            <span className="text-[#555]">· {capture.source_date}</span>
          )}
        </div>
        <h1 className="font-serif-display text-4xl sm:text-5xl lg:text-6xl tracking-tighter leading-tight">
          {capture.headline || <span className="text-[#555] italic">[ untitled ]</span>}
        </h1>
        <div className="text-[0.6rem] tracking-[0.25em] uppercase text-[#555]">
          filed by <span className="text-[#A3A3A3]">{capture.uploader_username || "—"}</span>
          {capture.uploader_alias && (<span className="text-[#555]"> · {capture.uploader_alias}</span>)}
          <span className="text-[#555]"> · {formatTs(capture.created_at)}</span>
        </div>
        {capture.tags && capture.tags.length > 0 && (
          <div className="flex flex-wrap gap-2 pt-2">
            {capture.tags.map((t) => (
              <Link
                key={t}
                to={`/the-terra-chronicle?tag=${encodeURIComponent(t)}`}
                className="inline-flex items-center border border-[#333] bg-[#0a0a0a] px-2 py-1 text-[0.6rem] tracking-[0.25em] uppercase text-[#A3A3A3] hover:text-[#E3E1D9] hover:border-[#E3E1D9]"
                data-testid={`terra-detail-tag-${t}`}
              >
                {t}
              </Link>
            ))}
          </div>
        )}
      </header>

      {/* Plates — full-width, click to lightbox */}
      {capture.images && capture.images.length > 0 ? (
        <section>
          <div className="section-label">
            <span>Plates · {capture.images.length}</span>
            <span>click to enlarge</span>
          </div>
          <div className="space-y-6">
            {capture.images.map((src, i) => (
              <button
                type="button"
                key={`${src.slice(-24)}-${i}`}
                onClick={() => setLightboxSrc(src)}
                className="block w-full border border-[#333] overflow-hidden cursor-zoom-in group bg-[#0a0a0a]"
                data-testid={`terra-detail-plate-${i}`}
              >
                <img
                  src={src}
                  alt={`plate-${i}`}
                  className="w-full h-auto tiv-img transition-opacity group-hover:opacity-90"
                />
              </button>
            ))}
          </div>
        </section>
      ) : (
        <div className="border border-dashed border-[#333] p-12 text-center text-[#555] text-xs tracking-widest uppercase">
          ::: no plates filed for this dispatch :::
        </div>
      )}

      {/* Caption */}
      {capture.caption ? (
        <section>
          <div className="section-label">
            <span>Note · Marginalia</span>
            <span>§ caption</span>
          </div>
          <div className="text-base leading-loose whitespace-pre-wrap max-w-3xl text-[#E3E1D9] font-serif-display">
            {capture.caption}
          </div>
        </section>
      ) : (
        <div className="text-[#555] text-xs tracking-widest uppercase">::: no marginalia :::</div>
      )}

      {/* Admin controls */}
      {isAdmin && (
        <div className="flex flex-wrap gap-3 border-t border-[#333] pt-8">
          <Link
            to="/the-terra-chronicle"
            state={{ openEditId: capture.id }}
            className="tiv-btn"
            data-testid="terra-detail-edit-link"
          >
            Amend Dispatch
          </Link>
          <button
            className="tiv-btn tiv-btn-danger"
            onClick={onDelete}
            data-testid="terra-detail-delete-btn"
          >
            Burn
          </button>
        </div>
      )}

      <div className="border-t border-[#333] pt-6 text-[0.6rem] tracking-[0.4em] uppercase text-[#555] text-center">
        <Link to="/the-terra-chronicle" className="hover:text-[#E3E1D9]">↩ Return to the chronicle</Link>
      </div>

      {lightboxSrc && <Lightbox src={lightboxSrc} onClose={() => setLightboxSrc(null)} />}
    </div>
  );
}
